import { motion } from "framer-motion";
import { AppLayout } from "../../../layout/AppLayout";
import { Crown, Live, Profile } from "../../../components/svg/GeneralIcons";
import "react-multi-carousel/lib/styles.css";
import { ProfileBadge } from "../../../components/world/rmc/ProfileBadge";
import { RMCCard } from "../../../components/world/rmc/card";
import clsx from "clsx";
import ProgressBar from "../../../components/world/rmc/ProgressBar";
import {
  Coins,
  Purse,
  Trophy,
} from "../../../components/svg/worldSvgs/rmc/profile";
import { Cheveron } from "../../../components/svg/worldSvgs/rmc/Achievements";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useContext, useEffect, useRef } from "react";
import {
  getFirstWordCapitalized,
  getValidImageUrl,
  ordinalSuffix,
} from "../../../utils";
import { useBrandLive } from "../../../api/brands/useBrandLive";
import { RmcContext } from "../../../components/context/RmcContext";
import { Avatar } from "../../../components/Avatar";
import { useGenerateBrand } from "../../../api/useGenerateBrand";
import { UserContext } from "../../../components/context/UseContext";
import { Loader } from "../../../components/loader/Loader";
import { FaPlane } from "react-icons/fa";

const ProfileStats = (data) => [
  { label: "Total pts", value: data?.points || 0 },
  { label: "Streak", value: `${data?.streak_count || 0} Days` },
  { label: "Badges", value: data?.badge_count || 0 },
];

function WorldProfile() {
  const navigate = useNavigate();
  const hasFetched = useRef(false);
  const { token } = useContext(UserContext);
  const {
    brandId,
    rmcAudience,
    slug,
    worldBranch,
    setTriviaId,
    setBrandId,
    setSlug,
    setWorldBranch,
    setRmcAudience,
  } = useContext(RmcContext);
  const { getLiveHistory, liveHistory, isLoading } = useBrandLive();
  const stats = ProfileStats(rmcAudience);
  const loadingLength = new Array(4).fill("_");

  const streakProgress =
    (rmcAudience?.streak_count / rmcAudience?.live_duration) * 100;

  useEffect(() => {
    if (brandId && !hasFetched.current) {
      hasFetched.current = true;
      getLiveHistory(brandId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const clearAllRmcData = () => {
    localStorage.removeItem("rmc_trivia_id");
    localStorage.removeItem("rmc_brand_id");
    localStorage.removeItem("world_slug");
    localStorage.removeItem("world_branch");
    localStorage.removeItem("rmc_audience");

    setTriviaId(null);
    setBrandId(null);
    setSlug(null);
    setWorldBranch(null);
    setRmcAudience(null);
  };

  const imageUrl = getValidImageUrl(
    rmcAudience?.audience_badge_image_url,
    "/image/BronzeMedal.png",
  );

  const {
    generateBrand,
    data: brandData,
    loading: isPending,
  } = useGenerateBrand();

  const primaryColor = brandData?.data?.brand?.secondary_color ?? "#FFD700";
  const iconBgColor = brandData?.data?.brand?.primary_color ?? "#534D26";

  useEffect(() => {
    if (brandId) {
      generateBrand({
        token: token,
        brand_id: brandId,
        is_link: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  if (isPending) {
    return (
      <div className="h-[100dvh] bg-[#111522] flex items-center justify-center">
        <Loader type="spinningBubbles" color="#fff" />
      </div>
    );
  }

  return (
    <AppLayout
      bg={"!bg-[#111522]"}
      navBg={"!bg-[#111522]"}
      firstText={brandData?.data?.brand?.name ?? "Kingdom Quest"}
      secondText={"I'm Live"}
      thirdText={"Achievements"}
      fourthText={"Profile"}
      firstIcon={
        brandData?.data?.brand?.name?.toLowerCase().includes("airpeace") ? (
          <FaPlane className="w-5 h-5 -rotate-45" />
        ) : (
          <Crown className={"w-5 h-5"} />
        )
      }
      secondIcon={<Live className={"w-5 h-5"} />}
      fourthIcon={<Profile className={"w-5 h-5"} />}
      activeColor={brandData?.data?.brand?.secondary_color ?? "#FFD700"}
      inactiveClass={"text-[#FFFFFFBF]"}
      firstLink={`${slug}/home`}
      secondLink={`${slug}/live${
        worldBranch?.value ? `?branch_id=${worldBranch.value}` : ""
      }`}
      thirdLink={`${slug}/achievements`}
      fourthLink={`${slug}/profile`}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 3, delay: 0.4 }}
        className="relative pb-20"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 3, delay: 0.4 }}
          className="px-5 pt-10"
        >
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="tracking-tighter text-2xl font-semibold text-white mx-0 text-center"
          >
            Profile
          </motion.h3>

          <>
            <div className="mb-12 mt-7 grid justify-items-center px-4">
              {rmcAudience?.user_image ? (
                <ProfileBadge
                  image={rmcAudience?.user_image}
                  className="w-24 h-24 p-[2px]"
                />
              ) : (
                <Avatar
                  firstName={rmcAudience?.first_name}
                  lastName={rmcAudience?.last_name}
                  size="xl"
                  customBgColor={primaryColor}
                  customTextColor="#000"
                />
              )}
              {rmcAudience?.audience_badge_name && rmcAudience?.rank && (
                <div className="bg-blackhue-rmc_hue py-2 px-4 rounded-[120px] flex justify-center items-center gap-1 mt-4">
                  <img
                    src={imageUrl}
                    alt={rmcAudience?.audience_badge_name}
                    className="w-3 h-3.5 object-contain"
                    loading="lazy"
                  />
                  <p className="text-[#FFFFFFBF] text-xs text-center font-medium ">
                    {`${rmcAudience?.audience_badge_name} | ${
                      rmcAudience?.rank
                        ? `${ordinalSuffix(rmcAudience?.rank)} Position`
                        : "Uranked"
                    }`}
                  </p>
                </div>
              )}
              <div
                onClick={() => {
                  clearAllRmcData();
                  navigate("/home");
                }}
                className="py-2 px-4 rounded-[10px] flex justify-center items-center gap-2.5 mb-4 cursor-pointer mt-2"
                style={{ backgroundColor: primaryColor }}
              >
                <Logout />
                <p className="text-black text-xs text-center font-medium ">
                  {`Leave ${getFirstWordCapitalized(slug)}`}
                </p>
              </div>
            </div>
            <div className="flex gap-4 justify-between items-center mb-5">
              {stats.map((x, i) => {
                return (
                  <RMCCard
                    key={i}
                    className={clsx(
                      " flex-1 text-center !py-3 !px-5 flex flex-col gap-1.5 border border-[#252B41]",
                    )}
                  >
                    <p className="text-white text-xl font-bold">{x.value}</p>
                    <span className="text-xs text-[#FFFFFF80] text-nowrap font-medium">
                      {x.label}
                    </span>
                  </RMCCard>
                );
              })}
            </div>

            <ProgressBar
              type="streak"
              title="Streak Progress"
              percentage={streakProgress.toFixed(0)}
              topLabelStyle={{ color: primaryColor }}
              strokeColor={primaryColor}
            />

            <div className="my-12">
              <p className="text-lg font-semibold text-white mb-4 ">
                Leaderboard
              </p>
              <RMCCard className="flex items-center justify-between gap-3 !py-2.5 !pl-2.5 !pr-7">
                <div className="flex items-center gap-2.5">
                  <Trophy color={primaryColor} bgColor={iconBgColor} />

                  <div>
                    <p className="text-base font-semibold text-white mb-1">
                      Top 50
                    </p>
                    <p className="text-xs font-medium text-[#FFFFFF80] leading-tight ">
                      {rmcAudience?.rank
                        ? `Rank ${ordinalSuffix(rmcAudience?.rank)}`
                        : "Uranked"}
                    </p>
                  </div>
                </div>

                <Cheveron />
              </RMCCard>
            </div>

            <div className="flex items-center flex-col gap-4 mb-12">
              <div className="flex items-center justify-between w-full ">
                <p className="text-lg font-semibold text-white ">Wallet</p>
                <p
                  onClick={() =>
                    navigate(`/worlds/${slug}/achievements?tab=wallet`)
                  }
                  className="cursor-pointer text-xs font-semibold"
                  style={{ color: primaryColor }}
                >
                  View Wallet
                </p>
              </div>

              <RMCCard className="flex w-full items-center justify-between gap-3 !py-2.5 !pl-2.5 !pr-7 ">
                <div className="flex items-center gap-2.5">
                  <Coins color={primaryColor} bgColor={iconBgColor} />

                  <div>
                    <p className="text-base font-semibold text-white mb-1">
                      Pts Balance
                    </p>
                    <p className="text-xs font-medium text-[#FFFFFF80] leading-tight ">
                      {rmcAudience?.points || 0} pts
                    </p>
                  </div>
                </div>

                <Cheveron />
              </RMCCard>

              <RMCCard className="flex w-full items-center justify-between gap-3 !py-2.5 !pl-2.5 !pr-7 ">
                <div className="flex items-center gap-2.5">
                  <Purse color={primaryColor} bgColor={iconBgColor} />

                  <div>
                    <p className="text-base font-semibold text-white mb-1">
                      Cash Balance
                    </p>
                    <p className="text-xs font-medium text-[#FFFFFF80] leading-tight ">
                      ₦{rmcAudience?.wallet_balance}
                    </p>
                  </div>
                </div>

                <Cheveron />
              </RMCCard>
            </div>

            <div className="flex flex-col gap-4 mb-2">
              <p className="text-lg font-semibold text-white ">
                Check-In History
              </p>

              <div className="flex flex-col gap-4">
                {isLoading ? (
                  <div className="space-y-3 mt-4 px-3">
                    {loadingLength.map((_, i) => (
                      <div
                        key={i}
                        className="h-[56px] bg-gray-600 rounded-lg animate-pulse"
                      ></div>
                    ))}
                  </div>
                ) : liveHistory.length === 0 ? (
                  <div className="flex justify-center items-center h-[25vh] flex-col gap-3">
                    <p className="tracking-tighter text-xl font-semibold text-white mx-0 text-center">
                      No Live History
                    </p>
                    <p className="text-sm font-medium mt-1 text-[#FFFFFF80] w-full max-w-[250px] text-center">
                      You haven't checked in to any live sessions yet.
                    </p>
                  </div>
                ) : (
                  liveHistory?.map((x, i) => {
                    return (
                      <RMCCard
                        key={i}
                        className="flex w-full items-center justify-between gap-3 !py-2.5 !pl-2.5 !pr-7"
                      >
                        <div className="flex items-center gap-2.5">
                          <Purse color={primaryColor} bgColor={iconBgColor} />

                          <div>
                            <p className="text-base font-semibold text-white mb-1">
                              {x?.title}
                            </p>

                            <p className="text-xs font-medium text-[#FFFFFF80] leading-tight ">
                              {x?.date
                                ? dayjs(x?.date).format("D, MMM")
                                : "Today"}
                            </p>
                          </div>
                        </div>

                        <Cheveron />
                      </RMCCard>
                    );
                  })
                )}
              </div>
            </div>
          </>
        </motion.div>
      </motion.div>
    </AppLayout>
  );
}

export default WorldProfile;

const Logout = () => {
  return (
    <svg
      width="18px"
      height="18px"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15 12L2 12M2 12L5.5 9M2 12L5.5 15"
        stroke="#000"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M9.00195 7C9.01406 4.82497 9.11051 3.64706 9.87889 2.87868C10.7576 2 12.1718 2 15.0002 2L16.0002 2C18.8286 2 20.2429 2 21.1215 2.87868C22.0002 3.75736 22.0002 5.17157 22.0002 8L22.0002 16C22.0002 18.8284 22.0002 20.2426 21.1215 21.1213C20.3531 21.8897 19.1752 21.9862 17 21.9983M9.00195 17C9.01406 19.175 9.11051 20.3529 9.87889 21.1213C10.5202 21.7626 11.4467 21.9359 13 21.9827"
        stroke="#000"
        stroke-width="1.5"
        stroke-linecap="round"
      />
    </svg>
  );
};
